import React, { useEffect, useMemo, useRef, useState } from 'react'
import isNil from 'lodash/isNil'
import { Button, Drawer, Select, Spin, Tooltip } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  CloudOutlined,
  CopyOutlined,
  HistoryOutlined,
  LockOutlined,
  SafetyOutlined,
} from '@ant-design/icons'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import {
  accessControlApi,
  type ImpliedRoleDto,
  type RoleDto,
} from '../api/access-control.client'
import { roleDetailQueryKey } from '../hooks/useRoleDetailQuery'
import { USERS_QUERY_KEY } from '../hooks/useUsersQuery'
import { useAuditLogQuery } from '../hooks/useAuditLogQuery'
import { MY_ABILITY_QUERY_KEY } from '../../../rbac/useMyAbilityQuery'
import { useSubjectsQuery } from '../hooks/useSubjectsQuery'
import {
  directActionsFor,
  formatLabel,
  getAssignableRoleNames,
  getImpliedRoleName,
  MANAGE_ACTION,
  RbacRoleName,
} from '../constants'
import { notifyError, notifySuccess } from '../utils/notify'
import RoleChip from './RoleChips/RoleChip'
import { addRoleKeepingImplied } from '../utils/roleSelection'
import { cognitoEmailDiffers, cognitoRoleDiffers } from '../utils/email'
import { formatCognitoRole } from '../constants/cognito-roles'
import StatusBadge from './StatusBadge'
import UserAccessHistory from './UserAccessHistory'
import styles from './ManageAccessDrawer.module.scss'

function CopyButton({ value, title }: { value: string; title: string }) {
  return (
    <Tooltip title={title}>
      <Button
        type="text"
        size="small"
        className={styles.copyBtn}
        icon={<CopyOutlined />}
        onClick={() => navigator.clipboard?.writeText(value)}
      />
    </Tooltip>
  )
}

const HISTORY_LIMIT = 50

type DrawerTab = 'access' | 'history'

interface InheritedEntry {
  actions: Set<string>
  roles: Set<string>
  manage: boolean
}

interface Props {
  cognitoUserId: string
  personUserId?: string
  email?: string
  cognitoEmail?: string
  firstName?: string
  lastName?: string
  status: string
  cognitoRole?: string
  cognitoUserRole?: string
  initialRoleIds: string[]
  impliedRole?: ImpliedRoleDto | null
  allRoles: RoleDto[]
  // View-only mode for users without Manage on PermissionManagement: no role
  // add/remove, no save.
  readOnly?: boolean
  open: boolean
  onClose: () => void
}

export default function ManageAccessDrawer({
  cognitoUserId,
  personUserId,
  email,
  cognitoEmail,
  firstName,
  lastName,
  status,
  cognitoRole,
  cognitoUserRole,
  initialRoleIds,
  impliedRole,
  allRoles,
  readOnly = false,
  open,
  onClose,
}: Props) {
  const queryClient = useQueryClient()
  const [roleIds, setRoleIds] = useState<string[]>(initialRoleIds)
  const [seeded, setSeeded] = useState(false)
  const [activeTab, setActiveTab] = useState<DrawerTab>('access')
  const history = useAuditLogQuery({
    targetId: cognitoUserId,
    page: 1,
    limit: HISTORY_LIMIT,
  })

  const initialRoleIdsKey = initialRoleIds.join(',')
  const seededKey = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      seededKey.current = null
      setSeeded(false)
      return
    }
    const sessionKey = personUserId ?? '__new__'
    if (seededKey.current === sessionKey) return
    seededKey.current = sessionKey
    setRoleIds(initialRoleIdsKey ? initialRoleIdsKey.split(',') : [])
    setSeeded(true)
  }, [open, personUserId, initialRoleIdsKey])

  // Whether the user already had explicitly granted RBAC roles when the drawer
  // opened. `initialRoleIds` is a stable snapshot for the session (the drawer is
  // keyed by cognitoUserId), so this reflects their state "before open".
  const hadExplicitRoles = initialRoleIds.length > 0

  // The implied role for this user's Cognito role. Prefer the API-provided value
  // (present only when the user had no grants at load); otherwise derive it from
  // the Cognito role so that removing every role locally still previews the
  // fallback the backend would apply on save.
  const effectiveImpliedRole = useMemo<ImpliedRoleDto | null>(() => {
    if (impliedRole) return impliedRole
    const name = getImpliedRoleName(cognitoRole)
    if (!name) return null
    const match = allRoles.find((r) => r.name === name)
    return match ? { id: match.id, name: match.name } : null
  }, [impliedRole, cognitoRole, allRoles])

  // The "Employee" role, resolved from the catalog so we have its id for both
  // the payload and its permission preview.
  const employeeRole = useMemo(
    () => allRoles.find((r) => r.name === RbacRoleName.EMPLOYEE),
    [allRoles]
  )

  // The unremovable fallback chip shown when no role is selected, or null when
  // the user has one or more explicit roles selected. When the admin clears
  // every role:
  // - User who STARTED with explicit roles → explicitly downgraded to the
  //   Employee role, which IS persisted to the API (`persisted: true`).
  // - User who STARTED with no explicit role → the (display-only) implied role,
  //   which is never persisted. Unchanged behavior.
  const fallbackRole = useMemo<{
    id: string
    name: string
    persisted: boolean
  } | null>(() => {
    if (roleIds.length > 0) return null
    if (hadExplicitRoles) {
      return employeeRole
        ? { id: employeeRole.id, name: employeeRole.name, persisted: true }
        : null
    }
    return effectiveImpliedRole
      ? {
          id: effectiveImpliedRole.id,
          name: effectiveImpliedRole.name,
          persisted: false,
        }
      : null
  }, [roleIds.length, hadExplicitRoles, employeeRole, effectiveImpliedRole])

  const usesEmployeeFallback = fallbackRole?.persisted === true
  const usesImpliedRole = fallbackRole !== null && !fallbackRole.persisted

  // The implied role was promoted to a real chip when the first role was added.
  const keepsImpliedRole =
    !hadExplicitRoles &&
    !isNil(effectiveImpliedRole) &&
    roleIds.includes(effectiveImpliedRole.id)

  // Permissions shown in "Access by Menu" come from the granted roles, or — when
  // there are none — from the fallback role so its access is still visible.
  const effectiveRoleIds = useMemo(
    () => (fallbackRole ? [fallbackRole.id] : roleIds),
    [fallbackRole, roleIds]
  )

  // The Employee fallback is a real, persisted downgrade, so it goes in the
  // payload; the implied fallback is display-only and saves nothing extra.
  const roleIdsToSave = useMemo(
    () => (fallbackRole?.persisted ? [fallbackRole.id] : roleIds),
    [fallbackRole, roleIds]
  )

  // Compare what Save would send with what the user has now, so the Save
  // button only appears once there is an actual change to persist.
  const isDirty = useMemo(() => {
    const saved = new Set(initialRoleIds)
    return (
      roleIdsToSave.length !== saved.size ||
      roleIdsToSave.some((id) => !saved.has(id))
    )
  }, [roleIdsToSave, initialRoleIds])

  const roleDetailResults = useQueries({
    queries: effectiveRoleIds.map((id) => ({
      queryKey: roleDetailQueryKey(id),
      queryFn: () => accessControlApi.getRoleDetail(id),
      enabled: open,
    })),
  })

  const { data: subjects = [], isLoading: subjectsLoading } = useSubjectsQuery()

  const isLoading =
    subjectsLoading || roleDetailResults.some((r) => r.isLoading)

  const roleNameMap = useMemo(() => {
    const map = Object.fromEntries(allRoles.map((r) => [r.id, r.name]))
    if (effectiveImpliedRole)
      map[effectiveImpliedRole.id] = effectiveImpliedRole.name
    return map
  }, [allRoles, effectiveImpliedRole])

  const inherited = useMemo<Record<string, InheritedEntry>>(() => {
    const map: Record<string, InheritedEntry> = {}
    roleDetailResults.forEach((result, index) => {
      const detail = result.data
      if (!detail) return
      const roleName = roleNameMap[effectiveRoleIds[index]] ?? detail.name
      detail.permissions.forEach((perm) => {
        if (!map[perm.subjectId]) {
          map[perm.subjectId] = {
            actions: new Set(),
            roles: new Set(),
            manage: false,
          }
        }
        map[perm.subjectId].roles.add(roleName)
        const grantsManage = perm.actions.some(
          (a) => a.toLowerCase() === MANAGE_ACTION.toLowerCase()
        )
        if (grantsManage) {
          map[perm.subjectId].manage = true
        }
        const granted = grantsManage
          ? directActionsFor(subjects, perm.subjectId)
          : perm.actions
        granted.forEach((a) => map[perm.subjectId].actions.add(a))
      })
    })
    return map
  }, [roleDetailResults, effectiveRoleIds, roleNameMap, subjects])

  const cardMenus = useMemo(() => Object.keys(inherited), [inherited])

  // Assignable roles are scoped to the target user's Cognito role (their
  // `dynamodb.persons.user_role`), not the logged-in admin's: a client admin
  // can only receive client-side RBAC roles, a Hixme admin only internal ones.
  const assignableNames = useMemo(
    () => getAssignableRoleNames(cognitoRole),
    [cognitoRole]
  )

  const availableRoles = useMemo(
    () =>
      allRoles.filter(
        (r) => !roleIds.includes(r.id) && assignableNames.has(r.name)
      ),
    [allRoles, roleIds, assignableNames]
  )

  const { mutate: save, isPending } = useMutation({
    mutationFn: async () => {
      await accessControlApi.updateUserAccess(cognitoUserId, {
        roleIds: roleIdsToSave,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY })
      // The edited user may be the current user — bust their own ability cache
      // so menus/route gates reflect the change now instead of after the ~5-min
      // staleTime. (Edits to other users self-heal on their next refetch/focus;
      // the server AbilityGuard is authoritative regardless.)
      queryClient.invalidateQueries({ queryKey: MY_ABILITY_QUERY_KEY })
      queryClient.invalidateQueries({
        queryKey: ['access-control', 'audit-log'],
      })
      notifySuccess('Access updated', 'Changes saved successfully.')
      onClose()
    },
    onError: () =>
      notifyError('Save failed', 'Could not update access. Please try again.'),
  })

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'User'

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      closable
      title={
        <div className={styles.header}>
          <span className={styles.eyebrow}>
            {readOnly ? 'View access' : 'Manage access'}
          </span>
          <span className={styles.userName}>{fullName}</span>
          {email && (
            <div className={styles.emailRow}>
              <span className={styles.emailLabel}>Profile</span>
              <span className={styles.email} title={email}>
                {email}
              </span>
              <CopyButton value={email} title="Copy profile email" />
            </div>
          )}
          {cognitoEmail && cognitoEmailDiffers({ email, cognitoEmail }) && (
            <div className={styles.emailRow}>
              <span className={`${styles.emailLabel} ${styles.signInLabel}`}>
                <CloudOutlined />
                Sign-in
              </span>
              <span
                className={`${styles.email} ${styles.signInEmail}`}
                title={cognitoEmail}
              >
                {cognitoEmail}
              </span>
              <CopyButton value={cognitoEmail} title="Copy sign-in email" />
            </div>
          )}
          {cognitoRoleDiffers({ cognitoRole, cognitoUserRole }) && (
            <div className={styles.emailRow}>
              <span className={`${styles.emailLabel} ${styles.signInLabel}`}>
                <CloudOutlined />
                Cognito role
              </span>
              <span
                className={`${styles.email} ${styles.signInEmail}`}
                title={cognitoUserRole}
              >
                {formatCognitoRole(cognitoUserRole ?? '')}
              </span>
            </div>
          )}
        </div>
      }
      extra={<StatusBadge status={status} />}
      footer={
        <div className={styles.footer}>
          {readOnly || !isDirty ? (
            <Button onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                loading={isPending}
                disabled={!seeded || isLoading}
                onClick={() => save()}
              >
                Save changes
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'access'}
          className={`${styles.tab} ${activeTab === 'access' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('access')}
        >
          <LockOutlined />
          Access
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'history'}
          className={`${styles.tab} ${activeTab === 'history' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <HistoryOutlined />
          History
          {!isNil(history.data) && (
            <span className={styles.tabCount}>· {history.data.total}</span>
          )}
        </button>
      </div>
      {activeTab === 'history' ? (
        <UserAccessHistory
          entries={history.data?.data ?? []}
          total={history.data?.total ?? 0}
          isLoading={history.isLoading}
          isError={history.isError}
        />
      ) : isLoading ? (
        <div className={styles.loading}>
          <Spin size="large" />
          <span className={styles.loadingText}>Loading access…</span>
        </div>
      ) : (
        <>
          {/* Roles */}
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Roles</span>
            <p className={styles.sectionDesc}>
              {usesEmployeeFallback
                ? 'This user has no granted role, so they default to the Employee role. Granting a role replaces it.'
                : usesImpliedRole
                  ? 'This user has no granted role, so the role below is implied. Adding a role keeps it as a regular role.'
                  : keepsImpliedRole && !isNil(effectiveImpliedRole)
                    ? `${effectiveImpliedRole.name} was implied for this user, so it is kept as a role and they keep their access. Remove it if they should not have it.`
                    : readOnly
                      ? 'Bundles of access granted to this user.'
                      : 'Bundles of access. Remove a role to revoke everything it grants below.'}
            </p>
            <div className={styles.roleChips}>
              {fallbackRole && (
                <RoleChip
                  tone="implied"
                  size="regular"
                  title={
                    usesEmployeeFallback
                      ? 'Default role applied when no other role is assigned'
                      : 'Implied from Cognito role. Kept as a role when you add one.'
                  }
                >
                  {fallbackRole.name}
                </RoleChip>
              )}
              {roleIds.map((id) => (
                <RoleChip
                  key={id}
                  tone="granted"
                  size="regular"
                  className={readOnly ? undefined : styles.removable}
                >
                  {roleNameMap[id] ?? id}
                  {!readOnly && (
                    <button
                      type="button"
                      title="Remove role"
                      className={styles.roleChipRemove}
                      onClick={() =>
                        setRoleIds((prev) => prev.filter((r) => r !== id))
                      }
                    >
                      <CloseOutlined style={{ fontSize: 10 }} />
                    </button>
                  )}
                </RoleChip>
              ))}
              {!readOnly && availableRoles.length > 0 && (
                <Select
                  className={styles.addRole}
                  placeholder="+ Add role"
                  value={null}
                  size="small"
                  onChange={(id: string) =>
                    setRoleIds((prev) =>
                      addRoleKeepingImplied(prev, id, {
                        hadExplicitRoles,
                        impliedRoleId: effectiveImpliedRole?.id,
                      })
                    )
                  }
                  options={availableRoles.map((r) => ({
                    value: r.id,
                    label: r.name,
                  }))}
                />
              )}
            </div>
          </div>

          {/* Access by Menu — view only, granted entirely by the roles above */}
          <div className={styles.menuSection}>
            <span className={styles.sectionLabel}>Access by Menu</span>
            <p className={styles.sectionDesc}>
              Permissions are granted by the roles above.
              {!readOnly &&
                ' To change what this user can do, add or remove a role.'}
            </p>

            {cardMenus.length === 0 ? (
              <div className={styles.emptyCards}>
                {readOnly
                  ? 'No access yet.'
                  : 'No access yet. Assign a role above to grant permissions.'}
              </div>
            ) : (
              <div className={styles.cards}>
                {cardMenus.map((menu) => {
                  const lockedRoles = inherited[menu]?.roles
                  const manageOn = inherited[menu]?.manage ?? false
                  // Roles store API action strings whose casing may differ from
                  // the catalog labels — match case-insensitively so a granted
                  // action always shows, displayed with its canonical label.
                  const grantedLower = new Set(
                    Array.from(inherited[menu]?.actions ?? []).map((a) =>
                      a.toLowerCase()
                    )
                  )
                  return (
                    <div key={menu} className={styles.menuCard}>
                      <div className={styles.menuCardHead}>
                        <span className={styles.menuTitle}>
                          {formatLabel(menu)}
                        </span>
                        {lockedRoles && lockedRoles.size > 0 && (
                          <span className={styles.lockedSource}>
                            <LockOutlined style={{ fontSize: 11 }} />
                            From {Array.from(lockedRoles).join(', ')}
                          </span>
                        )}
                      </div>
                      <div className={styles.pillRow}>
                        {manageOn && (
                          <span
                            className={`${styles.pill} ${styles.pillManageOn}`}
                          >
                            <SafetyOutlined style={{ fontSize: 12 }} />
                            Manage
                          </span>
                        )}
                        {directActionsFor(subjects, menu)
                          .filter((action) =>
                            grantedLower.has(action.toLowerCase())
                          )
                          .map((action) => (
                            <span
                              key={action}
                              className={`${styles.pill} ${manageOn ? styles.pillCovered : styles.pillLocked}`}
                            >
                              <CheckOutlined style={{ fontSize: 12 }} />
                              {formatLabel(action)}
                            </span>
                          ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}
    </Drawer>
  )
}
